import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import * as FileSaver from 'file-saver';
import { Subject, Subscription, from, of } from 'rxjs';
import { catchError, debounceTime, map, switchMap } from 'rxjs/operators';
import { ValueService } from '../../../shared/value.service';
import { Utils } from '../../../shared/utils';
import { consts } from '../../../consts';
import { Option } from '../../../shared/models/option.model';
import { ConfirmService } from '../../../modules_v2/shared/services/confirm.service';
import { NotificationService } from '../../../modules_v2/shared/services/notification.service';
import { LoadingDialogService } from '../../../shared/ui/loading/loading-dialog.service';
import { ModelTaxService } from '../model-tax.service';
import { Model182Service } from '../model-182.service';
import { TAX_FORM_DECLARATION_STATUS, TaxFormDeclarationModel } from '../tax-form-declaration.model';
import { TaxFormHistoryDialogComponent, TaxFormHistoryResult } from '../tax-form-history/tax-form-history-dialog.component';
import { TaxFieldOriginDialogComponent } from '../tax-field-origin/tax-field-origin-dialog.component';
import {
  Model182,
  Model182Validation,
  NATURALEZA_DECLARANTE_182,
  TIPO_DECLARACION_182,
  normalizarModelo182,
  nuevoModelo182
} from './taxes-182.model';
import { Model182DeclaradoDialogComponent } from './model-182-declarado-dialog/model-182-declarado-dialog.component';

const PERIODO_ANUAL = '0A';

@Component({
  selector: 'convfact-model182',
  templateUrl: './model-182.component.html',
  styleUrls: ['./model-182.component.css'],
  standalone: false
})
export class Model182Component implements OnInit, OnDestroy {
  KEY_PATH = 'PATH_URL_MODEL_182';

  path: string;
  isLoading = true;
  isSaving = false;
  mostrarGuia = false;

  data: Model182 = nuevoModelo182(new Date().getFullYear() - 1);
  validations: Model182Validation[] = [];
  declaracionActual: TaxFormDeclarationModel;

  ejercicioSel: string;
  readonly ejercicioOptions: Option[] = Utils.GetListYears()
    .map((anio: number) => ({ label: anio.toString(), value: anio.toString() }));

  readonly naturalezaOptions: Option[] = NATURALEZA_DECLARANTE_182;
  readonly tipoDeclaracionOptions: Option[] = TIPO_DECLARACION_182;

  readonly secciones: { clave: string, titulo: string }[] = [
    { clave: 'declarante', titulo: 'Declarante' },
    { clave: 'declaracion', titulo: 'Declaración' },
    { clave: 'resumen', titulo: 'Resumen de totales' },
    { clave: 'declarados', titulo: 'Declarados' },
    { clave: 'presentacion', titulo: 'Presentación' }
  ];

  private readonly campos: { clave: string, etiqueta: string, region: string }[] = [
    { clave: 'sujetoNIF', etiqueta: 'NIF', region: 'declarante' },
    { clave: 'sujetoDenominacion', etiqueta: 'Denominación de la entidad', region: 'declarante' },
    { clave: 'ejercicio', etiqueta: 'Ejercicio', region: 'declarante' },
    { clave: 'naturalezaDeclarante', etiqueta: 'Naturaleza del declarante', region: 'declarante' },
    { clave: 'telefono', etiqueta: 'Teléfono', region: 'declarante' },
    { clave: 'contacto', etiqueta: 'Persona con quien relacionarse', region: 'declarante' },
    { clave: 'sustitutiva', etiqueta: 'Tipo de declaración normal complementaria sustitutiva', region: 'declaracion' },
    { clave: 'justificanteAnterior', etiqueta: 'Justificante de la declaración anterior', region: 'declaracion' },
    { clave: 'justificante', etiqueta: 'Justificante de esta declaración', region: 'declaracion' },
    { clave: 'numeroRegistros', etiqueta: 'Número total de registros', region: 'resumen' },
    { clave: 'importeTotal', etiqueta: 'Importe total', region: 'resumen' },
    { clave: 'declarados', etiqueta: 'Declarados donantes', region: 'declarados' }
  ];

  private readonly KEY_REGIONES = 'cf-182-regiones';
  private readonly KEY_CONFIRMADAS = 'cf-182-confirmadas';

  regiones: { [region: string]: boolean } = {};
  confirmadas: { [region: string]: boolean } = {};
  seccionActiva = 'declarante';
  buscadorCampo: string;

  private readonly recalculo$ = new Subject<void>();
  private readonly subscripcion = new Subscription();
  private version = 0;
  private justificanteConsultado = '';

  constructor(
    private service: Model182Service,
    private draftService: ModelTaxService<any>,
    private valueService: ValueService,
    private dialog: MatDialog,
    private confirmService: ConfirmService,
    private notification: NotificationService,
    private loading: LoadingDialogService
  ) { }

  ngOnInit(): void {
    this.getPath();
    this.cargarRegiones();

    this.ejercicioSel = (new Date().getFullYear() - 1).toString();

    this.subscripcion.add(this.recalculo$.pipe(
      debounceTime(300),
      switchMap(() => {
        const enviada = this.version;

        return from(this.service.recalculate(this.payload())).pipe(
          map(respuesta => ({ respuesta, enviada })),
          catchError(() => {
            this.notification.warning('No se han podido recalcular los totales');
            return of(null);
          })
        );
      })
    ).subscribe(resultado => {
      if (resultado && resultado.enviada === this.version) {
        this.aplicarRecalculo(resultado.respuesta);
      }
    }));

    this.cargarEjercicio(true);
  }

  ngOnDestroy(): void {
    this.subscripcion.unsubscribe();
  }

  get ejercicio(): number {
    return Number(this.ejercicioSel) || this.data.ejercicio;
  }

  get tipoSel(): string {
    if (this.data.sustitutiva) return 'S';

    return this.data.complementaria ? 'C' : 'N';
  }

  get esAnterior(): boolean {
    return this.data.complementaria || this.data.sustitutiva;
  }

  get errores(): Model182Validation[] {
    return this.validations.filter(item => item.type !== 'WARNING');
  }

  get avisosValidacion(): Model182Validation[] {
    return this.validations.filter(item => item.type === 'WARNING');
  }

  get estadoDeclaracion(): string {
    if (!this.declaracionActual) return 'Sin guardar';
    if (this.declaracionActual.status === TAX_FORM_DECLARATION_STATUS.presented) return 'Presentada';
    if (this.declaracionActual.status === TAX_FORM_DECLARATION_STATUS.downloaded) return 'Fichero descargado';

    return 'Borrador guardado';
  }

  get varianteDeclaracion(): string {
    if (!this.declaracionActual) return 'neutral';
    if (this.declaracionActual.status === TAX_FORM_DECLARATION_STATUS.presented) return 'success';
    if (this.declaracionActual.status === TAX_FORM_DECLARATION_STATUS.downloaded) return 'info';

    return 'warning';
  }

  get nombreFichero(): string {
    const nif = (this.data.sujetoNIF || '').replace(/[^0-9A-Za-z]/g, '').toUpperCase();

    return [nif, this.ejercicioSel].filter(parte => !!parte).join('_') + '.182';
  }

  trackByIndex(i: number) { return i; }

  mensaje(clave: string): string {
    const item = this.validations.find(v => v.key === clave);

    return item ? item.message : '';
  }

  declaradoDe(item: Model182Validation): number {
    const coincidencia = /^declarados\[(\d+)\]\./.exec(item.key || '');

    return coincidencia ? Number(coincidencia[1]) : -1;
  }

  private payload(): Model182 {
    return {
      ...this.data,
      modelo: '182',
      ejercicio: this.ejercicio
    };
  }

  private programarRecalculo() {
    this.version++;
    this.recalculo$.next();
  }

  private aplicarRecalculo(respuesta: Model182) {
    const calculado = normalizarModelo182(respuesta);

    this.data = {
      ...this.data,
      declarados: calculado.declarados,
      numeroRegistros: calculado.numeroRegistros,
      importeTotal: calculado.importeTotal,
      avisos: calculado.avisos,
      justificante: calculado.justificante,
      justificanteAnterior: calculado.justificanteAnterior
    };
  }

  onNaturaleza(valor: string) {
    this.data.naturalezaDeclarante = valor;
    this.programarRecalculo();
  }

  onTipo(valor: string) {
    this.data.complementaria = valor === 'C';
    this.data.sustitutiva = valor === 'S';

    if (valor === 'N') {
      this.data.justificanteAnterior = '';
      this.justificanteConsultado = '';
    }

    this.programarRecalculo();
    this.traerJustificante();
  }

  onJustificanteAnterior(valor: string) {
    this.data.justificanteAnterior = valor;

    if ((valor || '').trim().length === 13) {
      this.traerJustificante();
    }
  }

  private traerJustificante() {
    const justificante = (this.data.justificanteAnterior || '').trim();

    if (!this.esAnterior || justificante.length !== 13 || justificante === this.justificanteConsultado) return;

    this.justificanteConsultado = justificante;

    this.loading.run('Buscando el justificante...',
      this.service.calculate(this.ejercicioSel, justificante, this.data.sustitutiva ? 'S' : 'C'))
      .then(modelo => {
        this.data.avisos = modelo.avisos;

        if (modelo.avisos.length) {
          this.notification.warning(modelo.avisos[0]);
        }
      })
      .catch(() => {
        this.justificanteConsultado = '';
        this.notification.error('No se ha podido comprobar el justificante');
      });
  }

  cambiarEjercicio() {
    this.cargarEjercicio(false);
  }

  private cargarEjercicio(primeraCarga: boolean) {
    const ejercicio = this.ejercicioSel;

    if (!ejercicio) return;

    this.isLoading = primeraCarga;

    const tarea = (async () => {
      const borradores = await this.draftService.getDrafts('182', ejercicio).catch(() => []);
      const existente = (borradores || [])
        .find(item => item.period === PERIODO_ANUAL && item.taxYear.toString() === ejercicio);

      if (existente) {
        const borrador = await this.draftService.getDraft('182', existente.uid) as Model182;

        if (this.esBorradorVigente(borrador)) {
          return borrador;
        }
      }

      return await this.service.calculate(ejercicio);
    })();

    const ejecucion = primeraCarga ? tarea : this.loading.run('Cargando el ejercicio...', tarea);

    ejecucion.then(modelo => {
      this.isLoading = false;
      this.aplicarModelo(modelo);
      this.loadDeclaracionActual();
    }).catch(() => {
      this.isLoading = false;
      this.notification.error('Ha ocurrido un error inesperado al obtener la información');
    });
  }

  private esBorradorVigente(borrador: Model182): boolean {
    return !!borrador && Array.isArray(borrador.declarados) && borrador.sujetoNIF !== undefined;
  }

  private aplicarModelo(modelo: Model182) {
    this.version++;
    this.data = normalizarModelo182(modelo);
    this.data.ejercicio = Number(this.ejercicioSel) || this.data.ejercicio;
    this.data.periodo = PERIODO_ANUAL;
    this.justificanteConsultado = this.esAnterior ? (this.data.justificanteAnterior || '').trim() : '';
    this.validations = [];

    if (this.data.avisos.length) {
      this.notification.warning(this.data.avisos[0]);
    }
  }

  private loadDeclaracionActual() {
    const ejercicio = this.ejercicioSel;

    this.draftService.getDrafts('182', ejercicio)
      .then(response => {
        this.declaracionActual = (response || [])
          .find(item => item.period === PERIODO_ANUAL && item.taxYear.toString() === ejercicio);
      })
      .catch(() => { });
  }

  calcular() {
    const justificante = this.esAnterior ? (this.data.justificanteAnterior || '').trim() : '';
    const tipo = this.data.sustitutiva ? 'S' : 'C';
    const previo = this.data;

    this.loading.run('Calculando el modelo...', this.service.calculate(this.ejercicioSel, justificante, tipo))
      .then(modelo => {
        this.aplicarModelo(modelo);

        ['telefono', 'contacto'].forEach(campo => {
          if (!(this.data as any)[campo] && (previo as any)[campo]) {
            (this.data as any)[campo] = (previo as any)[campo];
          }
        });
      })
      .catch(() => this.notification.error('Ha ocurrido un error inesperado al calcular el modelo'));
  }

  nuevoDeclarado() {
    this.abrirDeclarado(-1);
  }

  editarDeclarado(indice: number) {
    this.abrirDeclarado(indice);
  }

  private abrirDeclarado(indice: number) {
    const prefijo = `declarados[${indice}].`;
    const validaciones = indice < 0 ? [] : this.validations
      .filter(item => (item.key || '').startsWith(prefijo))
      .map(item => ({ ...item, key: item.key.substring(prefijo.length) }));

    Model182DeclaradoDialogComponent.open(this.dialog, {
      declarado: indice < 0 ? null : this.data.declarados[indice],
      ejercicio: this.ejercicio,
      validaciones
    }).subscribe(resultado => {
      if (!resultado) return;

      if (indice < 0) {
        this.data = { ...this.data, declarados: [...this.data.declarados, resultado.declarado] };
      } else {
        this.data = {
          ...this.data,
          declarados: this.data.declarados.map((item, i) => i === indice ? resultado.declarado : item)
        };
      }

      this.validations = this.validations.filter(item => this.declaradoDe(item) !== indice);
      this.notification.ok('Declarado guardado');
      this.programarRecalculo();
    });
  }

  eliminarDeclarado(indice: number) {
    const declarado = this.data.declarados[indice];

    if (!declarado) return;

    const persistido = declarado.origen === 'MANUAL' && !!declarado.uid;

    this.confirmService.open({
      title: 'Eliminar declarado',
      subtitle: declarado.nombre || declarado.nif || undefined,
      message: '¿Seguro que quieres quitar este declarado del modelo 182?',
      tone: 'danger',
      details: [
        { label: 'NIF', value: declarado.nif || '-' },
        { label: 'Clave', value: declarado.clave || '-' },
        { label: 'Importe', value: Utils.FormatMoney(declarado.importe) + ' €' }
      ],
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando declarado...',
      errorText: 'No se pudo eliminar el declarado',
      action: () => persistido ? this.service.deleteRecipient(declarado.uid) : Promise.resolve()
    }).subscribe(eliminado => {
      if (!eliminado) return;

      this.data = { ...this.data, declarados: this.data.declarados.filter((_, i) => i !== indice) };
      this.validations = [];
      this.notification.ok('Declarado eliminado');
      this.programarRecalculo();
    });
  }

  verOrigen(indice: number) {
    const declarado = this.data.declarados[indice];

    if (!declarado) return;

    this.loading.run('Buscando de dónde sale...', this.service.getOrigen(this.ejercicio, declarado))
      .then(origen => {
        if (!origen) {
          this.notification.warning('Este importe no tiene desglose');
          return;
        }

        this.dialog.open(TaxFieldOriginDialogComponent, {
          width: '720px',
          maxWidth: '96vw',
          maxHeight: '90vh',
          autoFocus: false,
          data: { origen }
        });
      })
      .catch(() => this.notification.warning('Este importe no tiene desglose'));
  }

  validate() {
    this.loading.run('Validando la declaración...', this.service.validate(this.payload()))
      .then(lista => {
        this.validations = lista;
        const errores = this.errores;

        if (!lista.length) {
          this.notification.ok('La declaración no tiene errores');
          return;
        }

        if (!errores.length) {
          this.notification.warning('Se puede presentar, pero hay avisos que revisar');
          return;
        }

        this.notification.warning(errores.length === 1 ? 'Hay 1 error que corregir' : `Hay ${errores.length} errores que corregir`);
        this.irAlCampo(errores[0]);
      })
      .catch(() => this.notification.error('Ha ocurrido un error inesperado al validar'));
  }

  irAlCampo(item: Model182Validation) {
    const indice = this.declaradoDe(item);

    if (indice >= 0) {
      this.abrirRegion('declarados');
      this.abrirDeclarado(indice);
      return;
    }

    if (item.key === 'declarados') {
      this.irALaSeccion('declarados');
      return;
    }

    if (item.key) {
      this.scrollToElement(item.key);
    }
  }

  getFile() {
    if (!Utils.IsTierFour()) {
      this.notification.warning(consts.UPDATE_SUBSCRIPTION_TIER_MESSAGE);
      return;
    }

    this.loading.run('Generando el fichero...', this.service.getFile(this.payload()))
      .then(resultado => {
        this.validations = [];
        FileSaver.saveAs(resultado.blob, resultado.nombre || this.nombreFichero);
        this.mostrarGuia = true;
        this.notification.ok('Fichero descargado');
        this.loadDeclaracionActual();
      })
      .catch(async err => {
        const cuerpo = await this.leerError(err);

        if (cuerpo) {
          this.mostrarFaltantes(cuerpo);
          return;
        }

        this.notification.error('Ha ocurrido un error inesperado al generar el fichero');
      });
  }

  private async leerError(err: any): Promise<any> {
    try {
      if (err?.error instanceof Blob) {
        return JSON.parse(await err.error.text());
      }

      return err?.error ?? null;
    } catch {
      return null;
    }
  }

  private mostrarFaltantes(respuesta: any) {
    this.validations = Array.isArray(respuesta?.value) ? respuesta.value : [];
    const faltan = this.errores.length;

    if (!faltan) {
      this.notification.error(respuesta?.error?.messages?.[0] || 'No se ha podido generar el fichero');
      return;
    }

    this.notification.warning(faltan === 1
      ? 'Hay 1 error que impide generar el fichero'
      : `Hay ${faltan} errores que impiden generar el fichero`);

    this.irAlCampo(this.errores[0]);
  }

  saveDraft() {
    this.isSaving = true;

    this.loading.run('Guardando el borrador...', this.draftService.saveDraft(
      { ...this.payload(), periodo: PERIODO_ANUAL },
      {
        taxYear: this.ejercicio,
        amount: this.data.importeTotal,
        declarationType: this.data.sustitutiva ? 'S' : this.data.complementaria ? 'C' : null,
        receiptNumber: this.esAnterior ? this.data.justificanteAnterior : ''
      }))
      .then(() => {
        this.isSaving = false;
        this.notification.ok('Borrador guardado');
        this.loadDeclaracionActual();
      })
      .catch(() => {
        this.isSaving = false;
        this.notification.error('No se ha podido guardar el borrador');
      });
  }

  openHistory() {
    this.dialog.open(TaxFormHistoryDialogComponent, {
      width: '960px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      autoFocus: false,
      data: { modelo: '182', titulo: 'modelo 182' }
    }).afterClosed().subscribe((result: TaxFormHistoryResult) => {
      if (result?.action === 'open') {
        this.openDeclaration(result.declaration);
      }
    });
  }

  private openDeclaration(declaration: TaxFormDeclarationModel) {
    this.loading.run('Abriendo la declaración...', this.draftService.getDraft('182', declaration.uid))
      .then(async (modelo: Model182) => {
        this.ejercicioSel = declaration.taxYear.toString();

        if (this.esBorradorVigente(modelo)) {
          this.aplicarModelo(modelo);
        } else {
          this.notification.warning('Esta declaración se guardó con el formato anterior: se muestran los datos calculados');
          this.aplicarModelo(await this.service.calculate(this.ejercicioSel));
        }

        this.loadDeclaracionActual();
      })
      .catch(() => this.notification.error('No se ha podido abrir la declaración'));
  }

  importFile(event: any) {
    const file: File = event?.target?.files?.[0];

    if (!file) return;

    const reader = new FileReader();

    reader.onload = () => {
      this.loading.run('Importando el fichero...', this.service.importFile(reader.result as string))
        .then(modelo => {
          if (modelo.ejercicio && this.ejercicioOptions.some(item => item.value === modelo.ejercicio.toString())) {
            this.ejercicioSel = modelo.ejercicio.toString();
          }

          this.aplicarModelo(modelo);
          this.loadDeclaracionActual();
          this.notification.ok('Fichero importado');
        })
        .catch(err => this.notification.error(err?.error?.error?.messages?.[0] || 'El fichero no se ha podido importar'));
    };

    reader.onerror = () => this.notification.error('No se ha podido leer el fichero');
    reader.readAsText(file, 'ISO-8859-1');
    event.target.value = '';
  }

  marcarPresentada() {
    if (!this.declaracionActual) {
      this.notification.warning('Guarda el borrador antes de marcarlo como presentado');
      return;
    }

    this.confirmService.open({
      title: 'Marcar como presentada',
      subtitle: `Ejercicio ${this.ejercicioSel}`,
      message: 'Se guardará como presentada en el historial. Esto no presenta la declaración: la presentación se hace en la sede de la Agencia Tributaria.',
      icon: 'check_circle',
      tone: 'info',
      confirmText: 'Marcar como presentada',
      action: () => this.draftService.setDraftStatus('182', this.declaracionActual.uid,
        TAX_FORM_DECLARATION_STATUS.presented)
    }).subscribe(confirmed => {
      if (confirmed) {
        this.notification.ok('Declaración marcada como presentada');
        this.loadDeclaracionActual();
      }
    });
  }

  descartarBorrador() {
    if (!this.declaracionActual) return;

    const presentada = this.declaracionActual.status === TAX_FORM_DECLARATION_STATUS.presented;

    this.confirmService.open({
      title: 'Descartar borrador',
      subtitle: `Ejercicio ${this.ejercicioSel}`,
      message: presentada
        ? 'Esta declaración está marcada como presentada. Si la descartas, se borra del historial y el formulario vuelve a los datos calculados.'
        : 'Se borra lo guardado de este ejercicio y el formulario vuelve a los datos calculados a partir de tus datos.',
      icon: 'restore',
      tone: 'danger',
      confirmText: 'Descartar y reiniciar',
      action: () => this.draftService.deleteDraft('182', this.declaracionActual.uid)
    }).subscribe(confirmed => {
      if (!confirmed) return;

      this.declaracionActual = null;
      this.validations = [];
      this.mostrarGuia = false;
      this.recorridoInicial();
      this.notification.ok('Borrador descartado');
      this.cargarEjercicio(false);
    });
  }

  colapsada(region: string): boolean {
    return !!this.regiones[region];
  }

  confirmada(region: string): boolean {
    return !!this.confirmadas[region];
  }

  toggleRegion(region: string) {
    this.regiones[region] = !this.regiones[region];

    if (!this.regiones[region]) {
      this.seccionActiva = region;
    }

    this.guardarRegiones();
  }

  get todoCompactado(): boolean {
    return this.secciones.every(seccion => this.regiones[seccion.clave]);
  }

  alternarTodo() {
    const compactar = !this.todoCompactado;

    this.secciones.forEach(seccion => this.regiones[seccion.clave] = compactar);
    this.guardarRegiones();
  }

  private abrirRegion(region: string) {
    this.seccionActiva = region;

    if (this.regiones[region]) {
      this.regiones[region] = false;
      this.guardarRegiones();
    }
  }

  irALaSeccion(region: string) {
    this.abrirRegion(region);
    this.bajarALaSeccion(region);
  }

  confirmarRegion(region: string) {
    this.confirmadas[region] = true;
    this.regiones[region] = true;

    const posicion = this.secciones.findIndex(item => item.clave === region);
    const siguiente = this.secciones
      .slice(posicion + 1)
      .find(candidata => !this.confirmadas[candidata.clave]);

    if (siguiente) {
      this.regiones[siguiente.clave] = false;
    }

    const destino = siguiente ? siguiente.clave : region;

    this.seccionActiva = destino;
    this.guardarRegiones();
    this.bajarALaSeccion(destino);
  }

  private bajarALaSeccion(region: string) {
    setTimeout(() => {
      const element = document.getElementById(`region-${region}`);

      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }

  private normalizar(texto: string): string {
    return (texto || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
  }

  irAlCampoBuscado() {
    const buscado = this.normalizar(this.buscadorCampo);

    if (!buscado) return;

    const campo = this.campos.find(item => this.normalizar(item.clave) === buscado)
      || this.campos.find(item => this.normalizar(`${item.etiqueta} ${item.clave}`).includes(buscado));

    if (!campo) {
      this.notification.warning(`No hay ningún campo "${this.buscadorCampo}" en el modelo 182`);
      return;
    }

    this.scrollToElement(campo.clave);
  }

  scrollToElement(key: string) {
    if (!key) return;

    const campo = this.campos.find(item => item.clave === key);

    if (campo) {
      this.abrirRegion(campo.region);
    }

    setTimeout(() => {
      const element = document.getElementById(key);

      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  }

  private cargarRegiones() {
    try {
      const regiones = localStorage.getItem(this.KEY_REGIONES);
      const confirmadas = localStorage.getItem(this.KEY_CONFIRMADAS);

      this.confirmadas = confirmadas ? JSON.parse(confirmadas) : {};

      if (regiones) {
        this.regiones = JSON.parse(regiones);
        return;
      }
    } catch {
      this.confirmadas = {};
    }

    this.recorridoInicial();
  }

  private recorridoInicial() {
    this.regiones = {};
    this.confirmadas = {};

    this.secciones.forEach((seccion, indice) => this.regiones[seccion.clave] = indice > 0);
    this.seccionActiva = this.secciones[0].clave;

    this.guardarRegiones();
  }

  private guardarRegiones() {
    try {
      localStorage.setItem(this.KEY_REGIONES, JSON.stringify(this.regiones));
      localStorage.setItem(this.KEY_CONFIRMADAS, JSON.stringify(this.confirmadas));
    } catch { }
  }

  redirectFileTax() {
    window.open(this.path, '_blank');
  }

  getPath() {
    this.valueService.getValue(this.KEY_PATH)
      .then(response => {
        this.path = response?.data?.data;
      })
      .catch(() => { });
  }
}
